import React, { useState } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import EditAttributesModalView from "../edit-attributes-modal-view/edit-attributes-modal-view";
import { clearSelectedGraphic } from "../../services/actions/map";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import EditAttributesErrorModal from "../edit-attributes-modal-view/edit-attributes-error-modal/edit-attributes-error-modal";
import { updateAttributesModalFeatureAttributes } from "../../services/reducers/map";
import { fetchAllArcgisObjects } from "../../services/reducers/filters";
import {
  fetchMasterPlanModificationData,
  setSelectedPlots,
} from "../../services/reducers/master-plan-modification";
import {
  dateToTimestamp,
  isDateType,
} from "../edit-attributes-modal-view/edit-attributes-modal-handlers";
import { useCardNavigation } from "../../utils/cardNavigation";
import { getFid } from "../../utils/plot-utils";

const EditAttributesModal = () => {
  const { attributesModal, userCanEditAttributes, drawingToolsIsActive } =
    useAppSelector((state) => state.map);
  const attributes = attributesModal.selectedFeature?.attributes ?? {};
  const fieldsInfo = attributesModal.selectedFeature?.fieldsInfo ?? null;
  const layerName = attributesModal.selectedFeature?.layerName ?? "";
  const dispatch = useAppDispatch();
  const [errorModal, setErrorModal] = useState<{
    show: boolean;
    message: string;
  }>({ show: false, message: "" });
  const { selectedPlots } = useAppSelector(
    (state) => state.masterPlanModification,
  );
  const { selectedCard } = useAppSelector((state) => state.filters);
  const { closeCurrentCard } = useCardNavigation();

  if (!attributesModal.selectedFeature) return null;
  const canEdit =
    userCanEditAttributes &&
    Boolean(attributesModal.selectedFeature.fieldsInfo) &&
    drawingToolsIsActive;

  const handleSaveAttributes = async (
    editedAttributes: Record<string, any>,
  ) => {
    try {
      const changes: Record<string, any> = {};
      Object.keys(editedAttributes).forEach((key) => {
        const originalValue = attributes[key];
        let newValue = editedAttributes[key];
        if (newValue === "" && newValue !== originalValue) {
          newValue = null;
        }

        if (originalValue !== newValue) {
          const fieldInfo = fieldsInfo?.find(
            (field) => field.fieldName === key,
          );

          if (fieldInfo && isDateType(fieldInfo.type) && newValue) {
            const timestamp = dateToTimestamp(newValue);
            if (isNaN(timestamp)) {
              changes[key] = null;
            } else {
              changes[key] = timestamp;
            }
          } else {
            changes[key] = newValue;
          }
        }
      });

      const layerUrl = attributesModal.selectedFeature?.layerUrl;
      if (!layerUrl) {
        setErrorModal({ show: true, message: "Layer URL not found." });
        return;
      }

      const featureLayer = new FeatureLayer({
        url: layerUrl,
      });

      await featureLayer.load();

      const objectIdFieldName = featureLayer.objectIdField;
      const id = attributes[objectIdFieldName];

      const updateFeature = new Graphic({
        attributes: {
          [objectIdFieldName]: id,
          ...changes,
        },
      });

      await featureLayer.applyEdits({
        updateFeatures: [updateFeature],
      });

      dispatch(updateAttributesModalFeatureAttributes(changes));

      const currentAttributesFid = getFid(attributes);

      const updatedSelectedPlots = selectedPlots.filter(
        (plot) => plot.fid !== currentAttributesFid,
      );
      if (updatedSelectedPlots.length !== selectedPlots.length) {
        dispatch(setSelectedPlots(updatedSelectedPlots));
      }

      if (selectedCard?.fid === currentAttributesFid) {
        closeCurrentCard(currentAttributesFid);
      }

      await Promise.all([
        dispatch(fetchAllArcgisObjects()),
        dispatch(fetchMasterPlanModificationData()),
      ]);
    } catch (error: any) {
      const mainMessage = error?.message;
      const detailMessages = error?.details?.messages;

      let errorMessage = "Failed to save changes";

      if (mainMessage) {
        errorMessage = mainMessage;
        if (detailMessages?.length) {
          errorMessage += ` (${detailMessages.join(", ")})`;
        }
      } else if (detailMessages?.length) {
        errorMessage = detailMessages.join(", ");
      }

      setErrorModal({
        show: true,
        message: errorMessage,
      });
    }
  };

  const closeErrorModal = () => {
    setErrorModal({ show: false, message: "" });
  };

  return (
    <>
      <EditAttributesModalView
        layerName={layerName}
        attributes={attributes}
        onSave={handleSaveAttributes}
        onCancel={() => dispatch(clearSelectedGraphic())}
        userCanEditAttributes={canEdit}
        fieldsInfo={fieldsInfo}
      />
      {errorModal.show && (
        <EditAttributesErrorModal
          onClose={closeErrorModal}
          message={errorModal.message}
        />
      )}
    </>
  );
};

export default EditAttributesModal;
